/**
 * iter261 — GeoSpreadTab
 * ────────────────────────────────────────────────────────────────────
 * Super Admin: "📍 الانتشار الجغرافي لـ CARTAL" — real geographic reach
 * powered by Cloudflare `CF-IPCity` (Managed Transforms → Add visitor
 * location headers). No GPS, no external services. Data source is
 * strictly `/api/analytics/admin/geo-spread`; nothing is fabricated
 * client-side.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { apiClient } from "../../lib/api";
import { Loader2, MapPin, Users, Activity, Eye, Globe2, RefreshCw } from "lucide-react";

const PERIODS = [
  { id: "today",       ar: "اليوم" },
  { id: "7d",          ar: "٧ أيام" },
  { id: "30d",         ar: "٣٠ يومًا" },
  { id: "this_month",  ar: "هذا الشهر" },
  { id: "last_month",  ar: "الشهر الماضي" },
  { id: "custom",      ar: "فترة مخصصة" },
];

// Approximate city coordinates within the KSA SVG viewBox below.
// Cities NOT in this map still appear in the table; on the map they
// fall into the "غير محدد على الخريطة" legend so nothing is invented.
const CITY_COORDS = {
  "Riyadh":       [430, 355], "الرياض":       [430, 355],
  "Jeddah":       [200, 470], "جدة":         [200, 470],
  "Mecca":        [220, 475], "مكة":         [220, 475], "Makkah": [220, 475],
  "Medina":       [235, 380], "المدينة":     [235, 380], "Al Madinah": [235, 380],
  "Dammam":       [590, 320], "الدمام":      [590, 320],
  "Khobar":       [598, 328], "الخبر":       [598, 328], "Al Khobar": [598, 328],
  "Dhahran":      [592, 325], "الظهران":     [592, 325],
  "Taif":         [255, 495], "الطائف":      [255, 495], "Ta'if": [255, 495],
  "Buraydah":     [430, 260], "بريدة":       [430, 260],
  "Tabuk":        [180, 240], "تبوك":        [180, 240],
  "Abha":         [305, 620], "أبها":        [305, 620],
  "Khamis Mushait":[315, 625],"خميس مشيط":  [315, 625],
  "Hail":         [340, 245], "حائل":        [340, 245], "Ha'il": [340, 245],
  "Najran":       [400, 660], "نجران":       [400, 660],
  "Jazan":        [285, 660], "جازان":       [285, 660], "Jizan": [285, 660],
  "Yanbu":        [180, 375], "ينبع":        [180, 375],
  "Al Ahsa":      [580, 370], "الأحساء":     [580, 370], "Hofuf": [580, 370],
  "Al Jubail":    [585, 290], "الجبيل":      [585, 290], "Jubail": [585, 290],
  "Al Bahah":     [285, 550], "الباحة":      [285, 550],
  "Arar":         [420, 130], "عرعر":        [420, 130],
  "Sakakah":      [370, 155], "سكاكا":       [370, 155],
  "Qatif":        [592, 315], "القطيف":      [592, 315],
};

// Extremely simplified KSA outline (points are approximate; used
// purely as a visual reference frame — not an official cartographic
// boundary). Coordinates match the SVG viewBox 0 0 720 720.
const KSA_PATH = "M 155 130 L 250 105 L 340 110 L 430 115 L 505 125 L 555 155 L 585 195 L 600 235 L 615 285 L 620 335 L 610 380 L 585 405 L 545 415 L 500 430 L 460 470 L 430 520 L 405 570 L 380 620 L 340 665 L 300 680 L 265 665 L 245 630 L 235 590 L 220 555 L 200 525 L 180 495 L 165 460 L 150 425 L 145 380 L 155 330 L 145 285 L 150 235 L 155 190 Z";

function fmtInt(n) {
  return (typeof n === "number" ? n : 0).toLocaleString("ar-SA");
}

function fmtPct(n) {
  return `${(typeof n === "number" ? n : 0).toFixed(2)}%`;
}

function KpiCard({ icon: Icon, label, value, hint }) {
  return (
    <div className="surface rounded-xl p-4 flex items-start gap-3">
      <div className="p-2 rounded-lg bg-[#f5efdf]"><Icon className="w-5 h-5 text-[#8a6f2e]" /></div>
      <div className="flex-1">
        <div className="text-xs text-[#6b6b6b]">{label}</div>
        <div className="font-display text-2xl font-medium mt-1">{value}</div>
        {hint && <div className="text-[10px] text-[#8a8a8a] mt-1">{hint}</div>}
      </div>
    </div>
  );
}

export default function GeoSpreadTab() {
  const [period, setPeriod] = useState("30d");
  const [frm, setFrm] = useState("");
  const [to, setTo] = useState("");
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  const load = useCallback(async () => {
    setBusy(true); setErr(null);
    try {
      const params = { period };
      if (period === "custom") {
        if (!frm || !to) { setBusy(false); return; }
        params.frm = frm; params.to = to;
      }
      const { data } = await apiClient.get("/analytics/admin/geo-spread", { params });
      setData(data);
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message);
    } finally { setBusy(false); }
  }, [period, frm, to]);

  useEffect(() => { if (period !== "custom") load(); }, [period, load]);

  const totals = data?.totals || { unique_visitors: 0, sessions: 0, vehicle_views: 0, cities_reached: 0 };
  const rows = data?.by_city || [];
  const maxUv = useMemo(() => rows.reduce((m, r) => Math.max(m, r.uv || 0), 0) || 1, [rows]);

  // ── iter275 — DISPLAY-ONLY pagination for the city table ──────────
  // Requirement: never drop or rotate rows; render in batches of 100
  // with a "load more" button. This is strictly a presentation cap on
  // how many rows are painted at once — the underlying `rows` array,
  // the `totals.cities_reached` KPI, the map dots aggregation, and
  // the backend query all remain UNBOUNDED. The counter is derived
  // independently in the backend via a distinct-city aggregation and
  // is NOT affected by this variable.
  const PAGE_SIZE = 100;
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  // Reset the "how much am I showing" cursor whenever the source rows
  // reference changes (new period, refresh). Keeps the view stable
  // during simple re-renders while resetting on real reloads.
  useEffect(() => { setVisibleCount(PAGE_SIZE); }, [rows]);
  const visibleRows = useMemo(() => rows.slice(0, visibleCount), [rows, visibleCount]);
  const hasMore = rows.length > visibleCount;
  const remaining = Math.max(0, rows.length - visibleCount);

  const cityDots = useMemo(() => {
    const dots = [];
    const unmapped = [];
    for (const r of rows) {
      if (!r.city) continue;
      const c = CITY_COORDS[r.city] || CITY_COORDS[r.city.trim()];
      if (c) dots.push({ x: c[0], y: c[1], city: r.city, uv: r.uv });
      else unmapped.push(r);
    }
    return { dots, unmapped };
  }, [rows]);

  return (
    <div className="space-y-5" data-testid="geo-spread-tab">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h2 className="font-display text-2xl flex items-center gap-2">
          <MapPin className="w-5 h-5 text-[#8a6f2e]" />
          الانتشار الجغرافي لـ CARTAL
        </h2>
        <button onClick={load} disabled={busy} className="btn-ghost text-xs" data-testid="geo-refresh">
          <RefreshCw className={`w-3.5 h-3.5 ${busy ? "animate-spin" : ""}`} /> تحديث
        </button>
      </div>

      {/* Period picker */}
      <div className="flex flex-wrap gap-2" data-testid="geo-period-filter">
        {PERIODS.map((p) => (
          <button key={p.id} onClick={() => setPeriod(p.id)}
            data-testid={`geo-period-${p.id}`}
            className={`px-3.5 py-1.5 rounded-full text-xs font-bold border transition-all ${period === p.id ? "bg-[#0a0a0a] border-[#0a0a0a] text-white" : "bg-white border-[#0a0a0a14] text-[#3a3a3a] hover:border-[#c9a55c]"}`}>
            {p.ar}
          </button>
        ))}
      </div>
      {period === "custom" && (
        <div className="flex flex-wrap items-end gap-2 surface rounded-xl p-3" data-testid="geo-custom-range">
          <div>
            <label className="text-[11px] text-[#6b6b6b] block mb-1">من</label>
            <input type="date" value={frm} onChange={(e) => setFrm(e.target.value)} className="input-dark" />
          </div>
          <div>
            <label className="text-[11px] text-[#6b6b6b] block mb-1">إلى</label>
            <input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="input-dark" />
          </div>
          <button onClick={load} className="btn-primary text-xs" disabled={!frm || !to || busy}>عرض</button>
        </div>
      )}

      {err && <div className="rounded-xl p-3 text-sm text-red-700 border border-red-200 bg-red-50">{String(err)}</div>}

      {/* KPI cards */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3" data-testid="geo-kpis">
        <KpiCard icon={Users}    label="الزوار الفريدون"      value={fmtInt(totals.unique_visitors)} hint="جهاز متصفح مميز" />
        <KpiCard icon={Activity} label="الزيارات (الجلسات)"   value={fmtInt(totals.sessions)}         hint="جلسة مستقلة" />
        <KpiCard icon={Eye}      label="مشاهدات السيارات"     value={fmtInt(totals.vehicle_views)}    hint="فتح صفحة سيارة" />
        <KpiCard icon={Globe2}   label="عدد المدن الواصلة"     value={fmtInt(totals.cities_reached)}   hint="مدن مقدّرة من CF-IPCity" />
      </div>

      {/* Map + Table */}
      <div className="grid lg:grid-cols-5 gap-4">
        {/* SVG Map */}
        <div className="lg:col-span-2 surface rounded-xl p-3">
          <div className="text-xs text-[#6b6b6b] mb-2">خريطة الانتشار — المملكة العربية السعودية</div>
          <svg viewBox="0 0 720 720" className="w-full h-auto" data-testid="geo-map">
            <defs>
              <radialGradient id="cityGlow" cx="50%" cy="50%" r="50%">
                <stop offset="0%" stopColor="#c9a55c" stopOpacity="0.9" />
                <stop offset="100%" stopColor="#c9a55c" stopOpacity="0" />
              </radialGradient>
            </defs>
            <path d={KSA_PATH} fill="#f5efdf" stroke="#c9a55c" strokeWidth="1.5" />
            {cityDots.dots.map((d, i) => {
              const r = 6 + (d.uv / maxUv) * 22;
              return (
                <g key={`${d.city}-${i}`}>
                  <circle cx={d.x} cy={d.y} r={r * 1.4} fill="url(#cityGlow)" />
                  <circle cx={d.x} cy={d.y} r={5} fill="#0a4f3a" stroke="#fff" strokeWidth="1.5">
                    <title>{`${d.city}: ${fmtInt(d.uv)} زائر فريد`}</title>
                  </circle>
                  <text x={d.x} y={d.y - r - 3} textAnchor="middle" fontSize="10" fontWeight="700" fill="#0a0a0a">
                    {d.city}
                  </text>
                </g>
              );
            })}
          </svg>
          {cityDots.unmapped.length > 0 && (
            <div className="text-[11px] text-[#6b6b6b] mt-2">
              <span className="font-bold">غير محدد على الخريطة:</span>{" "}
              {cityDots.unmapped.map(r => `${r.city} (${fmtInt(r.uv)})`).join(" ، ")}
            </div>
          )}
        </div>

        {/* City table */}
        <div className="lg:col-span-3 surface rounded-xl p-3 overflow-x-auto">
          <div className="text-xs text-[#6b6b6b] mb-2 flex flex-wrap items-center justify-between gap-2">
            <span>
              المدن مرتّبة تنازليًا حسب الزوار الفريدين
              {rows.length === 0 && !busy ? " — لا توجد بيانات بعد." : ""}
            </span>
            {rows.length > 0 && (
              <span className="text-[11px] text-[#8a8a8a]" data-testid="geo-city-visible-count">
                عرض {Math.min(visibleCount, rows.length)} من {rows.length}
              </span>
            )}
          </div>
          <table className="w-full text-sm" data-testid="geo-city-table">
            <thead>
              <tr className="text-[11px] text-[#6b6b6b] border-b border-[#0a0a0a]/10">
                <th className="text-right py-2 px-2">المدينة</th>
                <th className="text-right py-2 px-2">الزوار الفريدون</th>
                <th className="text-right py-2 px-2">الزيارات</th>
                <th className="text-right py-2 px-2">مشاهدات السيارات</th>
                <th className="text-right py-2 px-2">نسبة الزوار</th>
              </tr>
            </thead>
            <tbody>
              {busy && rows.length === 0 ? (
                <tr><td colSpan={5} className="py-8 text-center text-[#6b6b6b]"><Loader2 className="w-4 h-4 animate-spin inline" /></td></tr>
              ) : rows.length === 0 ? (
                <tr><td colSpan={5} className="py-8 text-center text-[#6b6b6b]">لا بيانات ضمن الفترة المحددة.</td></tr>
              ) : visibleRows.map((r, i) => (
                <tr key={`${r.city || "unknown"}-${i}`} className="border-b border-[#0a0a0a]/5 hover:bg-[#f5efdf]/40"
                    data-testid={`geo-row-${r.city || "unknown"}`}>
                  <td className="py-2 px-2 font-semibold">
                    {r.city || <span className="text-[#8a8a8a]">غير محدد</span>}
                  </td>
                  <td className="py-2 px-2">{fmtInt(r.uv)}</td>
                  <td className="py-2 px-2">{fmtInt(r.sessions)}</td>
                  <td className="py-2 px-2">{fmtInt(r.views)}</td>
                  <td className="py-2 px-2">
                    <div className="flex items-center gap-2">
                      <div className="w-24 h-1.5 bg-[#0a0a0a0d] rounded overflow-hidden">
                        <div className="h-full bg-[#c9a55c]" style={{ width: `${Math.min(100, r.pct)}%` }} />
                      </div>
                      <span className="tabular-nums text-xs">{fmtPct(r.pct)}</span>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {/* Load-more control — purely a display cursor. Never touches
              rows/backend/counter. Advances by PAGE_SIZE (100). */}
          {hasMore && (
            <div className="mt-3 flex justify-center">
              <button
                type="button"
                onClick={() => setVisibleCount((v) => v + PAGE_SIZE)}
                className="btn-ghost text-xs"
                data-testid="geo-city-load-more"
              >
                المزيد ({Math.min(PAGE_SIZE, remaining)} مدينة إضافية · متبقٍ {remaining})
              </button>
            </div>
          )}
        </div>
      </div>

      <div className="text-[11px] text-[#8a8a8a] leading-relaxed">
        المدينة مقدّرة من رأس <code className="bg-[#0a0a0a08] px-1">CF-IPCity</code> الخاص بـCloudflare
        فقط، ولا تُعتبر موقعًا دقيقًا. لا يستخدم النظام GPS ولا يطلب إذن الموقع، ولا تُخزَّن عناوين IP.
        نسبة المدينة = الزوار الفريدون فيها ÷ إجمالي الزوار الفريدين في نفس الفترة × 100.
        الحسابات الإدارية (Admin / Super Admin / Staff / أي دور إداري جديد) مستبعدة تلقائيًا من هذه الإحصاءات.
      </div>

      <CloudflareDiag />
    </div>
  );
}

// ── iter262 — Cloudflare header diagnostic ─────────────────────────
// Super Admin can inspect the last request headers that reached the
// backend so we can verify whether `CF-IPCity` is actually being
// forwarded. Read-only, capped to 20 snapshots, no PII stored.
function CloudflareDiag() {
  const [open, setOpen] = React.useState(false);
  const [data, setData] = React.useState(null);
  const [busy, setBusy] = React.useState(false);
  const [err, setErr] = React.useState(null);

  const load = async () => {
    setBusy(true); setErr(null);
    try {
      const { data } = await apiClient.get("/analytics/admin/geo-spread/diag");
      setData(data);
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message);
    } finally { setBusy(false); }
  };

  return (
    <div className="surface rounded-xl p-3">
      <button
        onClick={() => { setOpen(!open); if (!open && !data) load(); }}
        className="text-xs font-bold flex items-center gap-2"
        data-testid="geo-diag-toggle"
      >
        <Globe2 className="w-3.5 h-3.5 text-[#8a6f2e]" />
        فحص رؤوس Cloudflare (تشخيص) {open ? "▲" : "▼"}
      </button>
      {open && (
        <div className="mt-3 space-y-2 text-[11px]" data-testid="geo-diag-panel">
          <div className="flex items-center gap-3">
            <button onClick={load} disabled={busy} className="btn-ghost text-[11px]">
              <RefreshCw className={`w-3 h-3 ${busy ? "animate-spin" : ""}`} /> تحديث
            </button>
            {data && (
              <span className="text-[#6b6b6b]">
                آخر {data.count} طلبات · بمدينة محدّدة: <b>{data.with_resolved_city}</b>
              </span>
            )}
          </div>
          {err && <div className="text-red-700">{String(err)}</div>}
          {!err && data?.recent?.length === 0 && (
            <div className="text-[#8a8a8a]">لا توجد طلبات مسجّلة بعد.</div>
          )}
          {!err && data?.recent?.map((r, i) => (
            <details key={i} className="border border-[#0a0a0a10] rounded p-2">
              <summary className="cursor-pointer">
                <span className="font-mono">{r.at}</span>
                {" — "}
                {r.resolved_city
                  ? <span className="text-green-700">✓ {r.resolved_city}</span>
                  : <span className="text-red-700">✗ لا مدينة</span>}
              </summary>
              <pre className="mt-2 text-[10px] bg-[#0a0a0a05] p-2 rounded overflow-x-auto">
{JSON.stringify(r.cf_headers, null, 2)}
              </pre>
            </details>
          ))}
          <div className="text-[10px] text-[#8a8a8a] leading-relaxed">
            إن كانت جميع الصفوف تظهر «✗ لا مدينة»:
            <br />
            • بيئة Preview — طبيعي، فهي ليست خلف Cloudflare cartal.sa.
            <br />
            • بيئة Production — CARTAL يمر بطبقتَي Cloudflare (cartal.sa ثم Emergent edge)،
            والطبقة الثانية تُعيد كتابة رؤوس <code>cf-*</code>. الحل: من Cloudflare Dashboard
            لـcartal.sa اذهب إلى Rules → <b>Transform Rules → Modify Request Header</b>،
            وأضف قاعدة تُعيّن <code>X-Cartal-Visitor-City = {`{cf.city}`}</code>
            (وبنفس النمط <code>X-Cartal-Visitor-Country</code> و <code>X-Cartal-Visitor-Region</code>).
            الرؤوس بغير بادئة <code>cf-</code> لا يُعاد كتابتها بواسطة الطبقة الثانية،
            وسيقرأها الـbackend كأولوية أولى تلقائيًا.
          </div>
        </div>
      )}
    </div>
  );
}
